import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { useAnalytics } from '@/hooks/use-analytics'
import { format } from 'date-fns'
import { useEffect, useRef } from 'react'
import { BarChart3, Calendar, FileText, RefreshCw } from 'lucide-react'
import {
  getMarketResearchReportAppMarketResearchReportsReportIdGetOptions,
} from '@/client/@tanstack/react-query.gen'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MarketIndicators } from './-components/market-indicators'
import { MarketReportContent } from './-components/market-report-content'
import { MarketResearchSkeleton } from './-components/market-research.skeleton'
import { SummaryContent } from './-components/summary-content'

function reportQueryOptions(reportId: number) {
  return {
    ...getMarketResearchReportAppMarketResearchReportsReportIdGetOptions({
      path: { report_id: reportId },
    }),
    retry: false,
    staleTime: 5 * 60 * 1000,
  }
}

export const Route = createFileRoute(
  '/_authed/_user/market-research/$marketResearchId',
)({
  component: MarketResearchReportPage,
  pendingComponent: MarketResearchSkeleton,
  params: {
    parse: ({ marketResearchId }) => ({ marketResearchId: Number(marketResearchId) }),
    stringify: ({ marketResearchId }) => ({ marketResearchId: String(marketResearchId) }),
  },
  beforeLoad: async ({ params, context }) => {
    try {
      const report = await context.queryClient.ensureQueryData(
        reportQueryOptions(params.marketResearchId),
      )
      return { getTitle: () => report.profileName || 'Report Details' }
    }
    catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: ReportNotFoundFallback,
  errorComponent: ReportNotFoundFallback,
})

function ReportNotFoundFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <h2 className="text-2xl font-semibold">Report Not Found</h2>
        <p className="text-muted-foreground">
          The report you&apos;re looking for doesn&apos;t exist or you
          don&apos;t have access to it.
        </p>
        <Button asChild>
          <Link to="/market-research">Back to Reports</Link>
        </Button>
      </div>
    </div>
  )
}

function MarketResearchReportPage() {
  const { marketResearchId } = Route.useParams()
  const { data: report } = useSuspenseQuery(reportQueryOptions(marketResearchId))
  const { capture } = useAnalytics()
  const viewedRef = useRef(false)

  useEffect(() => {
    if (viewedRef.current)
      return
    viewedRef.current = true
    capture('market_research_report_viewed', { report_id: marketResearchId })
  }, [capture, marketResearchId])

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary/10 p-3 shrink-0">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1.5">
              <h1 className="text-2xl font-bold tracking-tight">
                {report.profileName || 'Market Research Report'}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Badge>{report.industry || 'Industry'}</Badge>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  Created
                  {' '}
                  {format(new Date(report.createdAt ?? ''), 'MMM d, yyyy')}
                </span>
                {report.lastRefreshedAt && (
                  <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Last refreshed
                    {' '}
                    {format(new Date(report.lastRefreshedAt), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left column - Summary & KPIs */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-primary" />
                Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SummaryContent
                summary={report.summary ?? ''}
                assessment={report.overallAssessment ?? ''}
              />

              <div className="pt-2">
                <h3 className="text-sm font-medium mb-3 flex items-center">
                  Key Metrics
                </h3>
                <MarketIndicators
                  indicators={report.indicators ?? []}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column - Full Report */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center">
                <FileText className="h-5 w-5 mr-2 text-primary" />
                Complete Market Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MarketReportContent {...report} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
